import React, { useCallback, useEffect, useState } from "react";

import { Button, Icon, Modal, Table } from "semantic-ui-react";

import {
  MeLabVideoItem,
  RunSequence,
  ACTIONS,
  SEQUENCE_NAME,
} from "react-lib/apps/MEH/sequence/PatientQueue";

import { formatDatetime } from "react-lib/utils/dateUtils";

// ======================= Types =======================
type ModMeLabVideoQueueProps = {
  open: boolean;
  runSequence: RunSequence;
  onClose: () => void;
};

// ======================= Helpers =======================
const STATUS_LABELS: Record<string, string> = {
  generating_script: "กำลังสร้างบทพูด",
  processing: "กำลังสร้างวิดีโอ",
  complete: "เสร็จแล้ว",
};

const ID_CELL_STYLE = { fontSize: "0.78rem", wordBreak: "break-all" as const };

const ID_TEXT_STYLE = { color: "rgba(0,0,0,0.45)", fontSize: "0.78rem" };

// ======================= Component =======================
const ModMeLabVideoQueue = (props: ModMeLabVideoQueueProps) => {
  const [items, setItems] = useState<MeLabVideoItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const handleRefresh = useCallback(() => {
    setLoading(true);
    setError("");

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.FETCH_MELAB_VIDEO_LIST,
      onSuccess: (list: MeLabVideoItem[]) => {
        setItems(list);
        setLoading(false);
      },
      onError: (err: any) => {
        setError(typeof err === "string" ? err : "ดึงรายการไม่สำเร็จ");
        setLoading(false);
      },
    });
  }, []);

  useEffect(() => {
    if (props.open) {
      handleRefresh();
    }
  }, [props.open]);

  const renderContent = () => {
    if (loading) {
      return <div style={{ textAlign: "center", padding: "2rem" }}>กำลังโหลด...</div>;
    }

    if (error) {
      return <div style={{ color: "red", textAlign: "center", padding: "2rem" }}>{error}</div>;
    }

    if (!items.length) {
      return (
        <div style={{ textAlign: "center", padding: "2rem" }}>ไม่มีรายการใน 2 วันล่าสุด</div>
      );
    }

    return (
      <Table celled compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>ผู้ป่วย</Table.HeaderCell>
            <Table.HeaderCell>รายการ</Table.HeaderCell>
            <Table.HeaderCell>สถานะ</Table.HeaderCell>
            <Table.HeaderCell>ขั้นตอนล่าสุด</Table.HeaderCell>
            <Table.HeaderCell>เริ่มเมื่อ</Table.HeaderCell>
            <Table.HeaderCell>thread_id</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {items.map((item) => (
            <Table.Row key={item.report_id}>
              <Table.Cell>{item.patient_name || "-"}</Table.Cell>
              <Table.Cell>
                {item.report_name || "-"}
                {!!item.report_id && <span style={ID_TEXT_STYLE}> ({item.report_id})</span>}
              </Table.Cell>
              <Table.Cell>
                {STATUS_LABELS[item.generation_status_text] || item.generation_status_text}
              </Table.Cell>
              <Table.Cell style={{ wordBreak: "break-word" }}>
                {item.current_step_detail || "-"}
              </Table.Cell>
              <Table.Cell>{formatDatetime(item.created_at, true) || "-"}</Table.Cell>
              <Table.Cell style={ID_CELL_STYLE}>{item.thread_id || "-"}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    );
  };

  return (
    <Modal className="centered" open={props.open} onClose={props.onClose} size="large" closeIcon>
      <Modal.Header style={{ fontSize: "1.28rem", padding: "0.9rem 1.4rem" }}>
        สถานะ Video{!loading && !error ? ` — ${items.length} รายการ` : ""}
      </Modal.Header>

      <Modal.Content style={{ maxHeight: "65vh", overflowY: "auto" }}>
        {renderContent()}
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={props.onClose}>ปิด</Button>

        <Button color="blue" loading={loading} onClick={handleRefresh}>
          <Icon name="refresh" />
          รีเฟรช
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

ModMeLabVideoQueue.displayName = "ModMeLabVideoQueue";

export default React.memo(ModMeLabVideoQueue);
